// @store/Sales/Pos/SettlementPolicy/SettlementPolicySlice.ts

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '@API/axiosNeatex';
// import type { SettlementPolicyModel } from './SettlementPolicyModels';
import type { APIResponse } from '@store/Shared/shared';

export type SettlementPolicyStatus = 'Draft' | 'Active' | 'Inactive';

export interface SettlementPolicyLineModel {
  id?: string;

  paymentMethodId?: string;
  shortageSettlementTypeId?: string;
  overageSettlementTypeId?: string;

  maxAllowedShortage?: number;
  maxAllowedOverage?: number;

  // Returned from API (optional)
  paymentMethodName?: string;
  shortageSettlementTypeName?: string;
  overageSettlementTypeName?: string;
}

export interface SettlementPolicyModel {
  id?: string;
  name?: string;
  name2?: string;
  isActive?: boolean;

  policyLines?: SettlementPolicyLineModel[];
}

export const getAllSettlementPolicies = createAsyncThunk(
  'SettlementPolicy/getAll',
  async (_: void, { rejectWithValue }) => {
    try {
       // Swagger يظهر POST
      const { data } = await ntxAPI.get<APIResponse<SettlementPolicyModel[]> | SettlementPolicyModel[]>(
        'Pos/GetAllSettlementPolicies',
        {}
      );
      return (data as any)?.data ?? data;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to load policies');
    }
  }
);

export const getSettlementPolicyById = createAsyncThunk(
  'SettlementPolicy/getById',
  async (id: string, { rejectWithValue }) => {
    try {
      // Swagger يظهر POST
      const { data } = await ntxAPI.get<APIResponse<SettlementPolicyModel> | SettlementPolicyModel>(
        `Pos/GetSettlementPolicyById?Id=${id}`
      );
      return (data as any)?.data ?? data;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to load policy');
    }
  }
);

export const addSettlementPolicy = createAsyncThunk(
  'SettlementPolicy/add',
  async (item: SettlementPolicyModel, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<any>>('Pos/AddSettlementPolicy', item);
      return data;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to add policy');
    }
  }
);

export const updateSettlementPolicy = createAsyncThunk(
  'SettlementPolicy/update',
  async (item: SettlementPolicyModel, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<any>>('Pos/UpdateSettlementPolicy', item);
      return data;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to update policy');
    }
  }
);

export const deleteSettlementPolicy = createAsyncThunk(
  'SettlementPolicy/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<any>>('Pos/DeleteSettlementPolicy', { id });
      return data;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to delete policy');
    }
  }
);

const initialState: {
  list: SettlementPolicyModel[];
  byId: SettlementPolicyModel | null;
  loadingList: boolean;
  loadingById: boolean;
  saving: boolean;
  error: any | null;
} = {
  list: [],
  byId: null,
  loadingList: false,
  loadingById: false,
  saving: false,
  error: null
};

const slice = createSlice({
  name: 'SettlementPolicy',
  initialState,
  reducers: {
    clearSettlementPolicyById(state) {
      state.byId = null;
    }
  },
  extraReducers(builder) {
    builder
      // list
      .addCase(getAllSettlementPolicies.pending, state => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(getAllSettlementPolicies.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingList = false;
        state.list = action.payload ?? [];
      })
      .addCase(getAllSettlementPolicies.rejected, (state, action: PayloadAction<any>) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      // by id
      .addCase(getSettlementPolicyById.pending, state => {
        state.loadingById = true;
        state.error = null;
      })
      .addCase(getSettlementPolicyById.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingById = false;
        state.byId = action.payload ?? null;
      })
      .addCase(getSettlementPolicyById.rejected, (state, action: PayloadAction<any>) => {
        state.loadingById = false;
        state.error = action.payload;
      })

      // add/update/delete saving flag
      .addCase(addSettlementPolicy.pending, state => {
        state.saving = true;
        state.error = null;
      })
      .addCase(addSettlementPolicy.fulfilled, state => {
        state.saving = false;
      })
      .addCase(addSettlementPolicy.rejected, (state, action: PayloadAction<any>) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(updateSettlementPolicy.pending, state => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateSettlementPolicy.fulfilled, state => {
        state.saving = false;
      })
      .addCase(updateSettlementPolicy.rejected, (state, action: PayloadAction<any>) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(deleteSettlementPolicy.pending, state => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteSettlementPolicy.fulfilled, state => {
        state.saving = false;
      })
      .addCase(deleteSettlementPolicy.rejected, (state, action: PayloadAction<any>) => {
        state.saving = false;
        state.error = action.payload;
      });
  }
});

export const { clearSettlementPolicyById } = slice.actions;
export default slice.reducer;
